<script setup lang="ts">
    const props = defineProps<{
        controller: ReturnType<typeof useModal>;
    }>();

    const { cartItems, totalPrice, isEmpty, shippingTotal, subtotal } = useCart();
    const localePath = useLocalePath();
    const { formatLink } = useInternationalization(localePath);
    const { urlSegment } = useLanguage();
    const { t } = useI18n();

    const sideCartKey = ref(0);
    const closeButton = ref();

    watch(props.controller.isOpen, () => {
        sideCartKey.value += 1;
    });

    onUpdated(() => {
        if (props.controller.isOpenWithFocus.value && props.controller.isOpen.value) {
            if (closeButton.value) {
                closeButton.value.focus();
            }
        }

        if (!props.controller.isOpenWithFocus.value) {
            const shoppingCartButtonEl = document.getElementById('cart-button');
            shoppingCartButtonEl?.focus();
        }
    });

    const hasPromotion = ref();

    const showAnnouncement = ref(false);

    function checkForPromotion() {
        cartItems.value.forEach((element) => {
            if (isPromotionLineItem(element)) {
                hasPromotion.value = true;
                return;
            } else {
                hasPromotion.value = false;
            }
        });
    }

    function checkForSpecialProduct() {
        let promotionAmount = 0;
        cartItems.value.forEach((element) => {
            if (isPromotionLineItem(element)) {
                promotionAmount++;
            }
        });

        showAnnouncement.value = cartItems.value.some(
            (element) =>
                cartItems.value.length - promotionAmount > 1 && element.payload?.productNumber?.includes('MER0006')
        );
    }

    checkForPromotion();
    checkForSpecialProduct();

    watch(cartItems, () => {
        checkForPromotion();
        checkForSpecialProduct();
    });

    const checkoutUrl = useCheckoutUrl();
</script>

<template>
    <LayoutSidebar
        :key="sideCartKey"
        :controller="props.controller"
        side="right"
        :type="'cart'"
    >
        <aside class="cart-wrapper">
            <div class="cart-header">
                <h2
                    id="slide-over-title"
                    class="cart-headline"
                >
                    {{ t('cart.header') }}
                </h2>
                <div class="close-wrapper">
                    <SharedCloseButton
                        ref="closeButton"
                        :label="t('general.close')"
                        @click="props.controller.close"
                        @keydown.space="props.controller.closeWithFocus"
                    />
                </div>
            </div>

            <div class="content-wrapper">
                <div class="flow-root">
                    <template v-if="!isEmpty">
                        <ul
                            role="list"
                            class="cart-list"
                        >
                            <template
                                v-for="cartItem in cartItems"
                                :key="cartItem.id"
                            >
                                <li v-if="!isPromotionLineItem(cartItem)">
                                    <CheckoutCartItem :cart-item="cartItem" />
                                </li>
                            </template>
                        </ul>

                        <template v-if="hasPromotion"
                            ><ul
                                role="list"
                                class="cart-list promotion-list"
                            >
                                <template
                                    v-for="cartItem in cartItems"
                                    :key="cartItem.id"
                                >
                                    <li v-if="isPromotionLineItem(cartItem)">
                                        <CheckoutCartItem
                                            :cart-item="cartItem"
                                            :is-side-cart="true"
                                        />
                                    </li>
                                </template>
                            </ul>
                        </template>
                    </template>
                    <div
                        v-else
                        class="empty-text"
                    >
                        <p class="cart-icon">🛒</p>
                        <br />
                        <p>{{ t('cart.emptyCartLabel') }}</p>
                        <br />
                        <p class="cart-subtext">{{ t('cart.change') }}</p>
                    </div>
                </div>
            </div>
        </aside>

        <aside
            aria-label="Side Cart Links"
            class="cart-footer"
        >
            <AnnouncementMessage
                v-if="showAnnouncement"
                :key="totalPrice"
            >
                <p>
                    {{ t('cart.shoe.shoeText1') }} <b>The Health Family Sneaker</b>
                    {{ t('cart.shoe.shoeText2') }}
                </p>
            </AnnouncementMessage>

            <FreeShipping
                v-else
                :key="subtotal"
            />

            <div class="sub-price-wrapper">
                <div class="price-element">
                    <p>{{ t('cart.subtotal') }}</p>
                    <SharedPrice :value="subtotal" />
                </div>
                <div class="price-element">
                    <p>{{ t('cart.shippingCost') }}</p>
                    <SharedPrice :value="shippingTotal" />
                </div>
            </div>

            <div class="total-price-wrapper">
                <div class="price-element total">
                    <p>{{ t('cart.totalPrice') }}*</p>
                    <SharedPrice
                        :value="totalPrice"
                        data-testid="cart-subtotal"
                    />
                </div>
            </div>

            <p class="sub-header-info">{{ t('cart.subHeaderText') }}</p>

            <div class="checkout-button-wrapper">
                <PrimaryButton
                    :button-href="checkoutUrl"
                    :button-text="t('cart.links.checkout')"
                    class="checkout-button"
                    :disabled="isEmpty || showAnnouncement"
                    data-testid="cart-checkout-link"
                />
            </div>

            <div class="sub-links">
                <div class="sub-link-wrapper">
                    <NuxtLink
                        class="sub-link"
                        :to="formatLink(`/${urlSegment}/shop/checkout/cart`)"
                        data-testid="cart-checkout-shopping-cart"
                        @click="props.controller.close"
                    >
                        {{ t('cart.links.toCart') }}
                    </NuxtLink>
                </div>

                <p>oder</p>

                <div class="sub-link-wrapper">
                    <button
                        type="button"
                        class="sub-link"
                        data-testid="cart-continue-button"
                        @click="props.controller.close"
                    >
                        {{ t('cart.links.continue') }}
                    </button>
                </div>
            </div>
        </aside>
    </LayoutSidebar>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/navigation/layout-sidebar.scss';
</style>
